import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { MyDesignCard, useMyDesignsQuery } from '@/entities/my-design';
import { MY_DESIGNS_PATH } from '@/shared/config';
import { Button } from '@/shared/ui';
import { QuoteDesignActions } from './quote-design-actions';
import { QuoteDesignEmpty } from './quote-design-empty';
import { QuoteDesignsSkeleton } from './quote-designs-skeleton';

/** 이 화면에 싣는 최근 디자인 수 — 나머지는 "내 디자인 전체 보기"로 넘긴다. */
const RECENT_COUNT = 4;

/**
 * 최근 저장한 디자인 몇 개 — 내 디자인 화면과 같은 첫 페이지 캐시(designs.list)를 다시 쓴다.
 * 첫 읽기에 실패하면 사용자용 문장과 다시 시도만 남긴다.
 */
export const QuoteRecentDesigns = (): ReactElement => {
  const designsQuery = useMyDesignsQuery();
  const firstPage = designsQuery.data?.pages[0];

  if (firstPage === undefined) {
    if (designsQuery.isError) {
      return (
        <div
          role="alert"
          className="flex flex-col items-center gap-4 border-y border-foreground/10 py-16 text-center"
        >
          <p className="text-sm text-destructive">저장한 디자인을 불러오지 못했습니다.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void designsQuery.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      );
    }
    return <QuoteDesignsSkeleton />;
  }

  if (firstPage.items.length === 0) return <QuoteDesignEmpty />;

  const designs = firstPage.items.slice(0, RECENT_COUNT);
  const hasMore = firstPage.items.length > RECENT_COUNT || firstPage.nextCursor !== null;

  return (
    <div className="flex flex-col gap-8">
      <ul className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4">
        {designs.map((design) => (
          <li key={design.id}>
            <MyDesignCard design={design} actions={<QuoteDesignActions design={design} />} />
          </li>
        ))}
      </ul>
      {hasMore ? (
        <Link
          to={MY_DESIGNS_PATH}
          className="w-fit text-sm font-semibold underline underline-offset-4 hover:no-underline"
        >
          내 디자인 전체 보기
        </Link>
      ) : null}
    </div>
  );
};
